'use client';

import { useMemo } from 'react';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import type { SelectOption } from './Select';
import { badgeTone } from '../lib/badgeColors';
import { cn } from '../lib/cn';

type EffortSliderProps = {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  // 항상 [{ value: '', label: 모델 기본값 }, ...레벨(빠름→스마트 순)] 형태로 전달된다.
  options: SelectOption[];
  // 슬라이더 상단 캡션(예: "추론 강도").
  title: string;
  // 슬라이더 위 '자동(모델 위임)' 옵션의 접두 라벨(예: "자동").
  autoLabel: string;
  // 컴팩트 배지에서 auto(모델 기본값)일 때 쓸 짧은 라벨(예: "기본값"). 팝오버·툴팁은 전체 라벨 유지.
  compactAutoLabel?: string;
  // 빠름↔스마트 축 양끝 라벨.
  fasterLabel: string;
  smarterLabel: string;
  disabled?: boolean;
  // Popover 루트에 적용(배치용). 예: 모델 라벨 줄 우측 absolute.
  className?: string;
  buttonClassName?: string;
};

/**
 * Claude Code 앱의 reasoning-effort 컨트롤을 참고한 슬라이더.
 * 인라인은 현재값을 보여주는 컴팩트 버튼이고, 클릭하면 팝오버에
 * "자동(모델 기본값)" 옵션 + 빠름↔스마트 도트 슬라이더가 뜬다.
 */
export const EffortSlider = ({
  id,
  value,
  onChange,
  options,
  title,
  autoLabel,
  compactAutoLabel,
  fasterLabel,
  smarterLabel,
  disabled = false,
  className,
  buttonClassName,
}: EffortSliderProps) => {
  const defaultOption = options[0];
  const levels = useMemo(() => options.slice(1), [options]);

  const isAuto = value === '';
  const activeLevelIndex = levels.findIndex((level) => level.value === value);
  const currentLabel = isAuto
    ? (defaultOption?.label ?? '')
    : (levels[activeLevelIndex]?.label ?? defaultOption?.label ?? '');
  // 컴팩트 배지는 짧은 라벨(있으면)을, 팝오버 헤더/툴팁은 전체 라벨을 쓴다.
  const compactLabel = isAuto ? (compactAutoLabel ?? currentLabel) : currentLabel;

  // 키보드 화살표 이동을 위한 선형 시퀀스: [자동, ...레벨].
  const sequence = useMemo(() => ['', ...levels.map((level) => level.value)], [levels]);
  const currentSequenceIndex = isAuto ? 0 : activeLevelIndex + 1;

  const moveBy = (delta: number) => {
    const nextIndex = Math.min(sequence.length - 1, Math.max(0, currentSequenceIndex + delta));
    const nextValue = sequence[nextIndex];
    if (nextValue !== value) {
      onChange(nextValue);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        event.preventDefault();
        moveBy(1);
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        event.preventDefault();
        moveBy(-1);
        break;
      case 'Home':
        event.preventDefault();
        if (value !== '') onChange('');
        break;
      case 'End':
        event.preventDefault();
        if (sequence.length > 1) onChange(sequence[sequence.length - 1]);
        break;
      default:
        break;
    }
  };

  return (
    <Popover className={cn('inline-flex min-w-0 max-w-full', className)}>
      <PopoverButton
        id={id}
        disabled={disabled}
        title={`${title}: ${currentLabel}`}
        className={cn(
          'group/effort inline-flex max-w-full cursor-pointer items-center rounded-full',
          'focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-primary/20',
          'disabled:cursor-not-allowed disabled:opacity-50',
          buttonClassName,
        )}
      >
        <span
          className={cn(
            'inline-flex max-w-full items-center truncate rounded-full px-2 text-[11px] font-medium leading-5',
            'ring-1 ring-inset ring-transparent transition-all',
            'group-hover/effort:ring-base-content/15 group-data-[open]/effort:ring-base-content/25',
            isAuto ? badgeTone.neutral : badgeTone.primary,
          )}
        >
          <span className="truncate">{compactLabel}</span>
        </span>
      </PopoverButton>

      <PopoverPanel
        portal
        anchor={{ to: 'bottom start', gap: 4, padding: 8 }}
        className="z-[1040] w-64 rounded-field border border-base-content/10 bg-base-100 p-3 shadow-lg focus:outline-none"
      >
        <div className="mb-3 flex items-center justify-between gap-2">
          <span className="text-xs font-medium text-[var(--color-fg-subtle)]">{title}</span>
          <span className="text-sm font-semibold text-base-content">{currentLabel}</span>
        </div>

        <button
          type="button"
          role="radio"
          aria-checked={isAuto}
          onClick={() => onChange('')}
          className={cn(
            'mb-3 flex w-full items-center justify-center gap-1.5 rounded-field border px-3 py-1.5 text-xs transition-colors',
            isAuto
              ? 'border-primary/40 bg-primary/10 font-medium text-primary'
              : 'border-base-content/15 text-[var(--color-fg-muted)] hover:border-base-content/30 hover:text-base-content',
          )}
        >
          <span className="font-semibold">{autoLabel}</span>
          <span className="text-[var(--color-fg-subtle)]">·</span>
          <span className="truncate">{defaultOption?.label}</span>
        </button>

        <div className="mb-1.5 flex items-center justify-between text-[11px] text-[var(--color-fg-subtle)]">
          <span>{fasterLabel}</span>
          <span>{smarterLabel}</span>
        </div>

        <div
          role="radiogroup"
          aria-label={title}
          tabIndex={0}
          onKeyDown={handleKeyDown}
          className="group relative flex items-center justify-between rounded-full px-1 py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
        >
          <span className="pointer-events-none absolute inset-x-3 top-1/2 h-px -translate-y-1/2 bg-base-content/15" />
          {levels.map((level) => {
            const isActive = !isAuto && level.value === value;
            return (
              <button
                key={level.value}
                type="button"
                role="radio"
                aria-checked={isActive}
                aria-label={level.label}
                title={level.label}
                onClick={() => onChange(level.value)}
                className="relative z-10 flex h-6 w-6 items-center justify-center rounded-full"
              >
                <span
                  className={cn(
                    'rounded-full transition-all duration-150',
                    isActive
                      ? 'h-3 w-3 bg-primary ring-4 ring-primary/20'
                      : 'h-1.5 w-1.5 bg-base-content/30 group-hover:bg-base-content/45',
                  )}
                />
              </button>
            );
          })}
        </div>
      </PopoverPanel>
    </Popover>
  );
};
